import React, { useState } from 'react';
import { X, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { contactEmail } from '../data/siteData';

const MerchOrderModal = ({ item, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States',
    size: item.name.includes('Sticker') ? 'One Size' : 'M',
    quantity: 1
  });

  const sizes = ['S', 'M', 'L', 'XL', 'XXL'];
  const itemPrice = parseFloat(item.price.replace('$', ''));
  
  // USPS Standard Shipping Rates
  const calculateShipping = () => {
    const subtotal = itemPrice * formData.quantity;
    if (subtotal >= 40) return 0; // Free shipping over $40
    
    if (formData.country === 'United States') {
      return 5.00; // USPS First Class
    } else {
      return 15.00; // USPS International
    }
  };

  const shipping = calculateShipping();
  const subtotal = itemPrice * formData.quantity;
  const total = subtotal + shipping;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Send order email
    const emailBody = `
New Order from Static Rift Website:

Item: ${item.name}
${item.name.includes('Sticker') ? '' : `Size: ${formData.size}`}
Quantity: ${formData.quantity}
Price: $${itemPrice.toFixed(2)} x ${formData.quantity} = $${subtotal.toFixed(2)}
Shipping: $${shipping.toFixed(2)}${shipping === 0 ? ' (FREE SHIPPING)' : ''}
Total: $${total.toFixed(2)}

Customer Information:
Name: ${formData.name}
Email: ${formData.email}

Shipping Address:
${formData.address}
${formData.city}, ${formData.state} ${formData.zip}
${formData.country}

---
Customer will be redirected to PayPal to complete payment.
    `.trim();

    const orderMailtoUrl = `mailto:${contactEmail}?subject=New Order: ${item.name}&body=${encodeURIComponent(emailBody)}`;
    window.open(orderMailtoUrl, '_blank', 'noopener,noreferrer');
    window.open(`https://www.paypal.me/FullGaugeRecords/${total.toFixed(2)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="merch-order-modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="merch-order-modal">
        <button onClick={onClose} className="modal-close" data-testid="merch-modal-close-button" aria-label="Close order modal">
          <X className="h-6 w-6" />
        </button>

        <div className="modal-header">
          <h2 className="modal-title">Order: {item.name}</h2>
          <p className="modal-subtitle">Complete your order details</p>
        </div>

        <form onSubmit={handleSubmit} className="order-form" data-testid="merch-order-form">
          <div className="order-summary">
            <img src={item.image} alt={item.name} className="order-item-image" />
            <div className="order-item-details">
              <h3>{item.name}</h3>
              <p className="order-item-price">{item.price}</p>
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group full-width">
              <label>Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="form-input"
                data-testid="order-name-input"
              />
            </div>

            <div className="form-group full-width">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="form-input"
                data-testid="order-email-input"
              />
            </div>

            {!item.name.includes('Sticker') && (
              <div className="form-group">
                <label>Size *</label>
                <select
                  name="size"
                  value={formData.size}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="order-size-select"
                >
                  {sizes.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Quantity *</label>
              <input
                type="number"
                name="quantity"
                min="1"
                max="10"
                value={formData.quantity}
                onChange={handleChange}
                className="form-input"
                data-testid="order-quantity-input"
              />
            </div>

            <div className="form-group full-width">
              <label>Street Address *</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                className="form-input"
                data-testid="order-address-input"
              />
            </div>

            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                className="form-input"
                data-testid="order-city-input"
              />
            </div>

            <div className="form-group">
              <label>State/Province *</label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
                className="form-input"
                data-testid="order-state-input"
              />
            </div>

            <div className="form-group">
              <label>ZIP/Postal Code *</label>
              <input
                type="text"
                name="zip"
                value={formData.zip}
                onChange={handleChange}
                required
                className="form-input"
                data-testid="order-zip-input"
              />
            </div>

            <div className="form-group">
              <label>Country *</label>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="form-input"
                data-testid="order-country-select"
              >
                <option value="United States">United States</option>
                <option value="Canada">Canada</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="Australia">Australia</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="order-total-section" data-testid="order-total-section">
            <div className="order-total-row">
              <span>Subtotal:</span>
              <span data-testid="order-subtotal-value">${subtotal.toFixed(2)}</span>
            </div>
            <div className="order-total-row">
              <span>Shipping (USPS):</span>
              <span className={shipping === 0 ? 'free-shipping' : ''} data-testid="order-shipping-value">
                {shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}
              </span>
            </div>
            {subtotal < 40 && subtotal + (itemPrice * (10 - formData.quantity)) >= 40 && (
              <p className="shipping-tip">
                Add ${(40 - subtotal).toFixed(2)} more for FREE shipping!
              </p>
            )}
            <div className="order-total-row total">
              <span>Total:</span>
              <span data-testid="order-total-value">${total.toFixed(2)}</span>
            </div>
          </div>

          <Button type="submit" className="cta-button-primary w-full" data-testid="paypal-checkout-button">
            <Mail className="mr-2 h-5 w-5" />
            Continue to PayPal
          </Button>
          <p className="payment-note">
            You&apos;ll be redirected to PayPal to complete your payment securely
          </p>
        </form>
      </div>
    </div>
  );
};

export default MerchOrderModal;
